import React from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { User, Shield, Star, Trophy, Clock, Award, Target, Zap, Medal, Plus, Trash2, Edit2, Check, X, Lightbulb, Compass, Flame, Upload, Camera, Briefcase, GraduationCap, HelpCircle, Bot } from 'lucide-react';
import SkillsManager from '../components/officers/SkillsManager';
import SkillProgressionDisplay from '../components/skills/SkillProgressionDisplay';
import TrainingRequestDialog from '../components/skills/TrainingRequestDialog';
import TrainingManagement from '../components/skills/TrainingManagement';
import GameGuideDialog from '../components/profile/GameGuideDialog';

import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import UserManualSection from '../components/profile/UserManualSection';
import BotPersonalitySection from '../components/profile/BotPersonalitySection';
import BotInstructionsSection from '../components/profile/BotInstructionsSection';
import BotMemorySection from '../components/profile/BotMemorySection';
import GamificationStatsCard from '../components/gamification/GamificationStatsCard';

export default function Profile() {
  const queryClient = useQueryClient();
  const [editingDrive, setEditingDrive] = React.useState({ type: null, index: null, value: '' });
  const [newDriveItem, setNewDriveItem] = React.useState({ type: null, value: '' });
  const [selectedUserEmail, setSelectedUserEmail] = React.useState(null);
  const [avatarDialog, setAvatarDialog] = React.useState(false);
  const [uploadingAvatar, setUploadingAvatar] = React.useState(false);
  const [trainingDialog, setTrainingDialog] = React.useState(false);
  const [showProgression, setShowProgression] = React.useState(false);
  const [gameGuideOpen, setGameGuideOpen] = React.useState(false);
  const [driveVideoDialog, setDriveVideoDialog] = React.useState(false);
  const memorySectionRef = React.useRef(null);

  const { data: currentUser } = useQuery({
    queryKey: ['me'],
    queryFn: () => base44.auth.me()
  });

  const isAdmin = currentUser?.role === 'admin';

  const { data: allOfficers } = useQuery({
    queryKey: ['allOfficers'],
    queryFn: () => base44.entities.OfficerProfile.list(),
    enabled: isAdmin,
    initialData: []
  });

  const { data: users } = useQuery({
    queryKey: ['users'],
    queryFn: () => base44.entities.User.list(),
    enabled: isAdmin,
    initialData: []
  });

  // Handle URL params for officer selection and section focus
  const [autoOpenReview, setAutoOpenReview] = React.useState(false);
  
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const officerId = urlParams.get('officer');
    const section = urlParams.get('section');
    
    if (officerId && allOfficers?.length > 0) {
      const officer = allOfficers.find(o => o.id === officerId);
      if (officer) {
        setSelectedUserEmail(officer.user_email);
      }
    }
    
    // Set flag to auto-scroll to review button in memory section
    if (section === 'memory') {
      setAutoOpenReview(true);
    }
  }, [allOfficers]);

  const viewingUserEmail = selectedUserEmail || currentUser?.email;
  const user = isAdmin && selectedUserEmail ?
  users.find((u) => u.email === selectedUserEmail) :
  currentUser;

  const { data: profile } = useQuery({
    queryKey: ['profile', viewingUserEmail],
    queryFn: async () => {
      const profiles = await base44.entities.OfficerProfile.list();
      return profiles.find((p) => p.user_email === viewingUserEmail) || {
        callsign: user?.full_name?.split(' ')[0] || "Commander",
        rank_title: "Captain",
        total_xp: 2450,
        badges: ["Strategic Thinker", "Guardian", "First Responder", "Visionary"],
        mastery: [],
        autonomy: [],
        purpose: []
      };
    },
    enabled: !!viewingUserEmail
  });

  const { data: missions } = useQuery({
    queryKey: ['missions', viewingUserEmail],
    queryFn: async () => {
      const allMissions = await base44.entities.Mission.list();
      return allMissions.filter((m) =>
      m.assigned_to_email === viewingUserEmail ||
      m.sponsor_email === viewingUserEmail ||
      m.owner_email === viewingUserEmail
      );
    },
    enabled: !!viewingUserEmail,
    initialData: []
  });

  const updateProfileMutation = useMutation({
    mutationFn: (data) => {
      if (!profile?.id) {
        throw new Error('Profile ID not found');
      }
      console.log('Updating profile with data:', data);
      return base44.entities.OfficerProfile.update(profile.id, data);
    },
    onSuccess: (result) => {
      console.log('Profile updated successfully:', result);
      queryClient.invalidateQueries({ queryKey: ['profile', viewingUserEmail] });
    },
    onError: (error) => {
      console.error('Failed to update profile:', error);
      alert('Failed to save changes: ' + error.message);
    }
  });

  // Determine if the current admin user is a Commander in Chief
  const { data: currentUserProfile } = useQuery({
    queryKey: ['currentUserProfile', currentUser?.email],
    queryFn: async () => {
      const profiles = await base44.entities.OfficerProfile.list();
      return profiles.find((p) => p.user_email === currentUser?.email) || null;
    },
    enabled: !!currentUser?.email
  });
  const isCommanderInChief = currentUserProfile?.custom_role === 'Commander in Chief';

  // Load RBAC configs to check bot_skills_manage permission
  const { data: rbacConfigs = [] } = useQuery({
    queryKey: ['rbacConfigs'],
    queryFn: () => base44.entities.RBACConfig.list(),
    enabled: !!currentUserProfile
  });

  // Check if current user has bot_skills_manage permission via RBAC
  const canManageSkills = React.useMemo(() => {
    if (!currentUserProfile) return false;
    const userRole = currentUserProfile.custom_role;
    // Check custom RBAC config first
    const rbacConfig = rbacConfigs.find(c => c.role_name === userRole);
    if (rbacConfig?.permissions) {
      return rbacConfig.permissions.includes('bot_skills_manage');
    }
    // Fall back to defaults — only Commander in Chief has this by default
    return userRole === 'Commander in Chief';
  }, [currentUserProfile, rbacConfigs]);

  const canEdit = isCommanderInChief || !isAdmin || (isAdmin && !selectedUserEmail) || (isAdmin && selectedUserEmail === currentUser?.email);
  const isCommander = isAdmin || profile?.custom_role === 'Commander';
  const isViewingOwnProfile = !selectedUserEmail || selectedUserEmail === currentUser?.email;

  const addDriveItem = (type) => {
    if (!newDriveItem.value.trim()) return;
    const current = profile?.[type] || [];
    updateProfileMutation.mutate({
      [type]: [...current, newDriveItem.value]
    });
    setNewDriveItem({ type: null, value: '' });
  };

  const removeDriveItem = (type, index) => {
    const current = profile?.[type] || [];
    updateProfileMutation.mutate({
      [type]: current.filter((_, i) => i !== index)
    });
  };

  const startEditDriveItem = (type, index, value) => {
    setEditingDrive({ type, index, value });
  };

  const saveEditDriveItem = (type) => {
    if (!editingDrive.value.trim()) return;
    const current = profile?.[type] || [];
    const updated = [...current];
    updated[editingDrive.index] = editingDrive.value;
    updateProfileMutation.mutate({
      [type]: updated
    });
    setEditingDrive({ type: null, index: null, value: '' });
  };

  const cancelEditDriveItem = () => {
    setEditingDrive({ type: null, index: null, value: '' });
  };

  const marvelAvatars = [
  'https://images.unsplash.com/photo-1608889476518-738c9b1b3b1f?w=300&h=300&fit=crop',
  'https://images.unsplash.com/photo-1635863138275-d9b33299680b?w=300&h=300&fit=crop',
  'https://images.unsplash.com/photo-1612036782180-6f0b6cd846fe?w=300&h=300&fit=crop',
  'https://images.unsplash.com/photo-1569003339405-ea396a5a8a90?w=300&h=300&fit=crop',
  'https://images.unsplash.com/photo-1620799140188-3b2a02fd9a77?w=300&h=300&fit=crop',
  'https://images.unsplash.com/photo-1608889825103-eb5ed706fc64?w=300&h=300&fit=crop'];


  const dcAvatars = [
  'https://images.unsplash.com/photo-1531259683007-016a7b628fc3?w=300&h=300&fit=crop',
  'https://images.unsplash.com/photo-1608889825205-eebdb9fc5806?w=300&h=300&fit=crop',
  'https://images.unsplash.com/photo-1608889335941-32ac5f2041b9?w=300&h=300&fit=crop',
  'https://images.unsplash.com/photo-1531259683007-016a7b628fc3?w=300&h=300&fit=crop',
  'https://images.unsplash.com/photo-1601645191163-3fc0d5d64e35?w=300&h=300&fit=crop',
  'https://images.unsplash.com/photo-1608889476561-6242cfdbf622?w=300&h=300&fit=crop'];


  const handleSelectAvatar = (avatarUrl) => {
    updateProfileMutation.mutate({ avatar_url: avatarUrl });
    setAvatarDialog(false);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingAvatar(true);
    try {
      const { file_url } = await base44.integrations.Core.UploadFile({ file });
      updateProfileMutation.mutate({ avatar_url: file_url });
      setAvatarDialog(false);
    } catch (error) {
      alert('Failed to upload avatar');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const level = Math.floor((profile?.total_xp || 0) / 1000) + 1;
  const xpProgress = (profile?.total_xp || 0) % 1000 / 10;

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Admin User Selector */}
      {isAdmin &&
      <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-lg border border-slate-800">
          <div>
            <h3 className="text-sm font-semibold text-slate-300">Viewing Profile As Admin</h3>
            <p className="text-xs text-slate-500">Select an officer to view their profile</p>
          </div>
          <Select value={selectedUserEmail || currentUser?.email} onValueChange={(v) => setSelectedUserEmail(v === currentUser?.email ? null : v)}>
            <SelectTrigger className="w-[300px] bg-slate-950 border-slate-800">
              <SelectValue placeholder="Select officer" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={currentUser?.email}>My Profile ({currentUser?.full_name})</SelectItem>
              {allOfficers.
            filter((o) => o.user_email !== currentUser?.email).
            map((officer) => {
              const userData = users.find((u) => u.email === officer.user_email);
              return (
                <SelectItem key={officer.id} value={officer.user_email}>
                      {officer.callsign} - {userData?.full_name} ({officer.user_email})
                    </SelectItem>);

            })}
            </SelectContent>
          </Select>
        </div>
      }

      {/* Profile Header */}
      <Card className="glass-panel border-none overflow-hidden relative">
        <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-r from-indigo-600 to-blue-600 opacity-20" />
        <CardContent className="pt-20 px-8 pb-8 relative z-10">
          <div className="flex flex-col md:flex-row items-start md:items-end gap-6">
            <div className="w-32 h-32 rounded-2xl bg-slate-900 border-4 border-slate-800 flex items-center justify-center shadow-2xl relative overflow-hidden group">
              {profile?.avatar_url ?
              <img src={profile.avatar_url} alt="Avatar" className="w-full h-full object-cover" /> :

              <User className="w-16 h-16 text-slate-400" />
              }
              {canEdit &&
              <Button
                onClick={() => setAvatarDialog(true)}
                className="absolute inset-0 bg-slate-950/80 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-xs"
                variant="ghost">

                  <Camera className="w-4 h-4 mr-2" />
                  {profile?.avatar_url ? 'Change' : 'Add Photo'}
                </Button>
              }
            </div>
            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-3">
                <Badge variant="secondary" className="bg-indigo-500/20 text-indigo-400 border-none uppercase tracking-wider">
                  Level {level}
                </Badge>
                <Badge variant="outline" className="border-slate-600 text-slate-400">
                  {profile?.rank_title}
                </Badge>
                {profile?.custom_role &&
                <Badge variant="outline" className="border-cyan-600 text-cyan-400">
                    {profile.custom_role}
                  </Badge>
                }
                {profile?.is_bot && (
                  <Badge variant="outline" className="border-purple-600 text-purple-400 flex items-center gap-1">
                    <Bot className="w-3 h-3" />
                    AI Officer
                  </Badge>
                )}
                {profile?.is_bot && profile?.bot_owner_email && (
                  <Badge variant="outline" className="border-amber-600 text-amber-400">
                    Owner: {allOfficers.find(o => o.user_email === profile.bot_owner_email)?.callsign || profile.bot_owner_email}
                  </Badge>
                )}
              </div>
              <h1 className="text-yellow-500 text-4xl font-bold">{user?.full_name}</h1>
              <p className="text-slate-400 font-mono">Callsign: "{profile?.callsign}"</p>
            </div>
            <div className="text-right flex items-start gap-3">
              <div>
                <div className="text-sm text-slate-500 uppercase tracking-widest">Total XP</div>
                <div className="text-3xl font-bold text-amber-400 font-mono">{profile?.total_xp}</div>
              </div>
              <Button
                size="icon"
                variant="outline"
                onClick={() => setGameGuideOpen(true)} className="bg-yellow-400 text-teal-300 text-sm font-medium rounded-full inline-flex items-center justify-center gap-2 whitespace-nowrap focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border shadow-sm hover:text-accent-foreground w-10 h-10 border-indigo-500/30 hover:bg-indigo-500/10 hover:border-indigo-500/50 transition-all"

                title="How the game works">

                <span className="text-indigo-400 text-lg">?</span>
              </Button>
            </div>
          </div>

          <div className="mt-8 space-y-2">
            <div className="flex justify-between text-xs font-mono text-slate-400">
              <span>PROGRESS TO LEVEL {level + 1}</span>
              <span>{profile?.total_xp % 1000} / 1000 XP</span>
            </div>
            <Progress value={xpProgress} className="h-2 bg-slate-800" indicatorClassName="bg-amber-400" />
          </div>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-8">
        <Card className="glass-panel border-none relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-yellow-500/10 to-transparent rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl" />
          
          <CardHeader>
            <CardTitle className="text-slate-700 font-semibold tracking-tight leading-none flex items-center gap-2">
              <Shield className="w-5 h-5 text-yellow-400" />
              Service Commendations
            </CardTitle>
            <p className="text-xs text-slate-500 mt-2">Honors earned through exemplary service</p>
          </CardHeader>
          
          <CardContent>
            <div className="space-y-3">
              {profile?.badges?.map((badge, i) => {
                const icons = [Medal, Award, Star, Trophy];
                const BadgeIcon = icons[i % icons.length];
                const colors = [
                { bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', text: 'text-yellow-400', glow: 'shadow-yellow-500/20' },
                { bg: 'bg-indigo-500/10', border: 'border-indigo-500/30', text: 'text-indigo-400', glow: 'shadow-indigo-500/20' },
                { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400', glow: 'shadow-emerald-500/20' },
                { bg: 'bg-cyan-500/10', border: 'border-cyan-500/30', text: 'text-cyan-400', glow: 'shadow-cyan-500/20' }];

                const color = colors[i % colors.length];

                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.1 }}
                    className={`
                      relative p-4 rounded-xl border ${color.border} ${color.bg}
                      backdrop-blur-sm group hover:scale-[1.02] transition-all duration-300
                      shadow-lg hover:${color.glow}
                    `}>

                    <div className="flex items-center gap-4">
                      <div className={`p-3 rounded-xl ${color.bg} border ${color.border} ${color.text} group-hover:scale-110 transition-transform`}>
                        <BadgeIcon className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-slate-800 text-sm font-bold uppercase tracking-wide">
                          {badge}
                        </h4>
                        <p className="text-xs text-slate-500 mt-0.5 font-mono">
                          Badge #{String(i + 1).padStart(3, '0')}
                        </p>
                      </div>
                      <div className={`w-2 h-2 rounded-full ${color.text} animate-pulse`} />
                    </div>
                    
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                  </motion.div>);

              })}
            </div>
            
            <div className="mt-6 pt-6 border-t border-slate-800/50">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Total Commendations</span>
                <span className="text-yellow-400 font-bold font-mono">{profile?.badges?.length || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-panel border-none">
          <CardHeader>
            <CardTitle className="text-slate-700 font-semibold tracking-tight leading-none flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-400" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6 relative border-l border-slate-800 ml-3 pl-6">
              {[
              { action: "Mission Completed", detail: "Operation: Rosetta Stone", time: "2 hours ago", xp: "+500" },
              { action: "Red Line Alert", detail: "Proximity Warning", time: "Yesterday", xp: null },
              { action: "Promotion", detail: "Achieved Rank: Captain", time: "3 days ago", xp: null }].
              map((log, i) =>
              <div key={i} className="relative">
                  <div className="absolute -left-[29px] top-1 w-3 h-3 rounded-full bg-slate-800 border border-slate-600" />
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-slate-700 font-medium">{log.action}</p>
                      <p className="text-sm text-slate-500">{log.detail}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-600">{log.time}</p>
                      {log.xp && <p className="text-xs text-amber-400 font-bold">{log.xp}</p>}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Gamification Stats */}
      <GamificationStatsCard profile={profile} />

      {/* Officer's Drive */}
      <Card className="glass-panel border-none">
        <CardHeader>
          <CardTitle className="text-slate-700 font-semibold tracking-tight leading-none flex items-center gap-2">
            <Flame className="w-5 h-5 text-orange-400" />
            Officer's Drive
            <button
              onClick={() => setDriveVideoDialog(true)}
              className="ml-2 p-1 rounded-full bg-slate-200 hover:bg-indigo-100 transition-colors group"
              title="Learn about Officer's Drive">

              <HelpCircle className="w-4 h-4 text-slate-600 group-hover:text-indigo-600" />
            </button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Mastery */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Lightbulb className="w-4 h-4 text-amber-400" />
              <h4 className="text-slate-700 text-sm font-semibold uppercase tracking-wider">MASTERY</h4>
            </div>
            <div className="space-y-2">
              {(profile?.mastery || []).map((item, index) =>
              <div key={index} className="bg-slate-100 text-slate-50 p-2 rounded-lg flex items-center gap-2 border border-slate-800">
                  {editingDrive.type === 'mastery' && editingDrive.index === index ?
                <>
                      <Input data-testid="profile-profile-input"
                    value={editingDrive.value}
                    onChange={(e) => setEditingDrive({ ...editingDrive, value: e.target.value })}
                    className="flex-1 bg-slate-950 border-slate-700 h-8"
                    autoFocus />
                      <Button size="icon" variant="ghost" onClick={() => saveEditDriveItem('mastery')} className="h-8 w-8 text-emerald-400">
                        <Check className="w-4 h-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={cancelEditDriveItem} className="h-8 w-8 text-slate-500">
                        <X className="w-4 h-4" />
                      </Button>
                    </> :

                <>
                     <span className="text-slate-700 text-sm flex-1">{item}</span>
                     {canEdit &&
                  <>
                         <Button size="icon" variant="ghost" onClick={() => startEditDriveItem('mastery', index, item)} className="h-8 w-8 text-slate-500 hover:text-indigo-400">
                           <Edit2 className="w-3.5 h-3.5" />
                         </Button>
                         <Button size="icon" variant="ghost" onClick={() => removeDriveItem('mastery', index)} className="h-8 w-8 text-slate-500 hover:text-rose-400">
                           <Trash2 className="w-3.5 h-3.5" />
                         </Button>
                       </>
                  }
                   </>
                }
                </div>
              )}
              {canEdit && newDriveItem.type === 'mastery' ?
              <div className="flex items-center gap-2">
                  <Input data-testid="profile-profile-input-1"
                  value={newDriveItem.value}
                  onChange={(e) => setNewDriveItem({ ...newDriveItem, value: e.target.value })}
                  placeholder="What do you want to master?" className="bg-slate-950 text-slate-50 px-3 py-1 text-base rounded-md flex w-full border shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm flex-1 border-slate-700 h-8"

                  autoFocus
                  onKeyPress={(e) => e.key === 'Enter' && addDriveItem('mastery')} />
                  <Button size="sm" onClick={() => addDriveItem('mastery')} className="h-8 bg-indigo-600 hover:bg-indigo-700">Add</Button>
                  <Button size="sm" variant="ghost" onClick={() => setNewDriveItem({ type: null, value: '' })} className="h-8">Cancel</Button>
                </div> :
              canEdit &&
              <Button
                size="sm"
                variant="outline"
                onClick={() => setNewDriveItem({ type: 'mastery', value: '' })}
                className="w-full border-dashed border-slate-700 text-slate-400 hover:text-slate-200">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Mastery Goal
                </Button>
              }
            </div>
          </div>

          {/* Autonomy */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Compass className="w-4 h-4 text-cyan-400" />
              <h4 className="text-slate-700 text-sm font-semibold uppercase tracking-wider">AUTONOMY</h4>
            </div>
            <div className="space-y-2">
              {(profile?.autonomy || []).map((item, index) =>
              <div key={index} className="bg-slate-100 text-slate-700 p-2 rounded-lg flex items-center gap-2 border border-slate-800">
                  {editingDrive.type === 'autonomy' && editingDrive.index === index ?
                <>
                      <Input data-testid="profile-profile-input-2"
                    value={editingDrive.value}
                    onChange={(e) => setEditingDrive({ ...editingDrive, value: e.target.value })} className="bg-slate-950 text-slate-50 px-3 py-1 text-base rounded-md flex w-full border shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm flex-1 border-slate-700 h-8"

                    autoFocus />
                      <Button size="icon" variant="ghost" onClick={() => saveEditDriveItem('autonomy')} className="h-8 w-8 text-emerald-400">
                        <Check className="w-4 h-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={cancelEditDriveItem} className="h-8 w-8 text-slate-500">
                        <X className="w-4 h-4" />
                      </Button>
                    </> :

                <>
                      <span className="text-slate-700 text-sm flex-1">{item}</span>
                      {canEdit &&
                  <>
                          <Button size="icon" variant="ghost" onClick={() => startEditDriveItem('autonomy', index, item)} className="h-8 w-8 text-slate-500 hover:text-indigo-400">
                            <Edit2 className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => removeDriveItem('autonomy', index)} className="h-8 w-8 text-slate-500 hover:text-rose-400">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </>
                  }
                    </>
                }
                </div>
              )}
              {canEdit && newDriveItem.type === 'autonomy' ?
              <div className="flex items-center gap-2">
                  <Input data-testid="profile-profile-input-3"
                  value={newDriveItem.value}
                  onChange={(e) => setNewDriveItem({ ...newDriveItem, value: e.target.value })}
                  placeholder="Where do you want full autonomy?" className="bg-slate-950 text-slate-50 px-3 py-1 text-base rounded-md flex w-full border shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm flex-1 border-slate-700 h-8"

                  autoFocus
                  onKeyPress={(e) => e.key === 'Enter' && addDriveItem('autonomy')} />
                  <Button size="sm" onClick={() => addDriveItem('autonomy')} className="h-8 bg-indigo-600 hover:bg-indigo-700">Add</Button>
                  <Button size="sm" variant="ghost" onClick={() => setNewDriveItem({ type: null, value: '' })} className="h-8">Cancel</Button>
                </div> :
              canEdit &&
              <Button
                size="sm"
                variant="outline"
                onClick={() => setNewDriveItem({ type: 'autonomy', value: '' })}
                className="w-full border-dashed border-slate-700 text-slate-400 hover:text-slate-200">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Autonomy Goal
                </Button>
              }
            </div>
          </div>

          {/* Purpose */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Target className="w-4 h-4 text-purple-400" />
              <h4 className="text-slate-700 text-sm font-semibold uppercase tracking-wider">PURPOSE</h4>
            </div>
            <div className="space-y-2">
              {(profile?.purpose || []).map((item, index) =>
              <div key={index} className="bg-slate-100 p-2 rounded-lg flex items-center gap-2 border border-slate-800">
                  {editingDrive.type === 'purpose' && editingDrive.index === index ?
                <>
                      <Input data-testid="profile-profile-input-4"
                    value={editingDrive.value}
                    onChange={(e) => setEditingDrive({ ...editingDrive, value: e.target.value })}
                    className="flex-1 bg-slate-950 border-slate-700 h-8"
                    autoFocus />
                      <Button size="icon" variant="ghost" onClick={() => saveEditDriveItem('purpose')} className="h-8 w-8 text-emerald-400">
                        <Check className="w-4 h-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={cancelEditDriveItem} className="h-8 w-8 text-slate-500">
                        <X className="w-4 h-4" />
                      </Button>
                    </> :

                <>
                      <span className="text-slate-700 text-sm flex-1">{item}</span>
                      {canEdit &&
                  <>
                          <Button size="icon" variant="ghost" onClick={() => startEditDriveItem('purpose', index, item)} className="h-8 w-8 text-slate-500 hover:text-indigo-400">
                            <Edit2 className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => removeDriveItem('purpose', index)} className="h-8 w-8 text-slate-500 hover:text-rose-400">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </>
                  }
                    </>
                }
                </div>
              )}
              {canEdit && newDriveItem.type === 'purpose' ?
              <div className="flex items-center gap-2">
                  <Input data-testid="profile-profile-input-5"
                  value={newDriveItem.value}
                  onChange={(e) => setNewDriveItem({ ...newDriveItem, value: e.target.value })}
                  placeholder="What is your personal purpose?" className="bg-slate-950 text-slate-50 px-3 py-1 text-base rounded-md flex w-full border shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm flex-1 border-slate-700 h-8"

                  autoFocus
                  onKeyPress={(e) => e.key === 'Enter' && addDriveItem('purpose')} />
                  <Button size="sm" onClick={() => addDriveItem('purpose')} className="h-8 bg-indigo-600 hover:bg-indigo-700">Add</Button>
                  <Button size="sm" variant="ghost" onClick={() => setNewDriveItem({ type: null, value: '' })} className="h-8">Cancel</Button>
                </div> :
              canEdit &&
              <Button
                size="sm"
                variant="outline"
                onClick={() => setNewDriveItem({ type: 'purpose', value: '' })}
                className="w-full border-dashed border-slate-700 text-slate-400 hover:text-slate-200">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Purpose Goal
                </Button>
              }
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Officer's Skills & Proficiencies */}
      <Card className="glass-panel border-none">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-slate-700 font-semibold tracking-tight leading-none flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-indigo-400" />
              Skills & Proficiencies
            </CardTitle>
            <p className="text-xs text-slate-500 mt-2">Officer's expertise and skill development</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowProgression(!showProgression)}>

              {showProgression ? 'Show Manager' : 'Show Progression'}
            </Button>
            {isViewingOwnProfile &&
            <Button
              size="sm"
              onClick={() => setTrainingDialog(true)}
              className="bg-indigo-600 hover:bg-indigo-700">

                <GraduationCap className="w-4 h-4 mr-2" />
                Request Training
              </Button>
            }
          </div>
        </CardHeader>
        <CardContent>
          {showProgression ?
          <SkillProgressionDisplay skills={profile?.skills || []} /> :

          <SkillsManager
            skills={profile?.skills || []}
            onChange={(skills) => updateProfileMutation.mutate({ skills })}
            readonly={!isAdmin && profile?.custom_role !== 'Commander' && profile?.custom_role !== 'Officer'} />

          }
        </CardContent>
      </Card>

      {/* Training Management */}
      {isViewingOwnProfile &&
      <TrainingManagement
        isCommander={isCommander}
        currentUserEmail={currentUser?.email} />

      }

      {/* Officer's Personal User Manual or Bot Personality */}
      {profile?.is_bot ? (
        <>
          <BotPersonalitySection
            profile={profile}
            canEdit={canEdit}
            onUpdate={(data) => updateProfileMutation.mutate(data)}
          />
          <BotInstructionsSection profile={profile} isOwner={profile?.bot_owner_email === currentUser?.email || isAdmin} />
          <BotMemorySection 
            profile={profile} 
            currentUserEmail={currentUser?.email}
            isOwner={profile?.bot_owner_email === currentUser?.email || isAdmin}
            canManageSkills={canManageSkills}
            autoOpenReview={autoOpenReview}
          />
        </>
      ) : (
        <UserManualSection
          profile={profile}
          canEdit={canEdit}
          onUpdate={(data) => updateProfileMutation.mutate(data)}
        />
      )}




      {/* Officer's Missions */}
      <Card className="glass-panel border-none">
        <CardHeader>
          <CardTitle className="text-slate-700 font-semibold tracking-tight leading-none flex items-center gap-2">
            <Target className="w-5 h-5 text-indigo-400" />
            Officer's Missions, Objectives & Activities
          </CardTitle>
        </CardHeader>
        <CardContent>
          {missions.length === 0 ?
          <div className="text-center py-8 text-slate-500">
              No missions assigned yet
            </div> :

          <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-900/50 border-b border-slate-800">
                  <tr>
                    <th className="bg-slate-50 text-slate-400 p-3 font-medium text-left">Mission</th>
                    <th className="bg-slate-100 text-slate-400 p-3 font-medium text-left">Type</th>
                    <th className="bg-slate-100 text-slate-400 p-3 font-medium text-left">Role</th>
                    <th className="bg-slate-100 text-slate-400 p-3 font-medium text-left">Status</th>
                    <th className="bg-slate-100 text-slate-400 p-3 font-medium text-right">XP</th>
                  </tr>
                </thead>
                <tbody>
                  {missions.map((mission) => {
                  const role = mission.sponsor_email === viewingUserEmail ? 'Sponsor' :
                  mission.owner_email === viewingUserEmail ? 'Owner' :
                  mission.assigned_to_email === viewingUserEmail ? 'Assigned' : 'Unknown';

                  return (
                    <tr key={mission.id} className="border-b border-slate-800 hover:bg-slate-900/30">
                        <td className="text-slate-700 p-3">{mission.title}</td>
                        <td className="p-3">
                          <Badge variant="outline" className={`
                            text-xs
                            ${mission.game_mode === 'campaign' ? 'text-indigo-400 border-indigo-400/30' : ''}
                            ${mission.game_mode === 'rank_and_file' ? 'text-slate-400 border-slate-400/30' : ''}
                            ${mission.game_mode === 'special_ops' ? 'text-rose-400 border-rose-400/30' : ''}
                          `}>
                            {mission.game_mode.replace(/_/g, ' ')}
                          </Badge>
                        </td>
                        <td className="text-slate-700 p-3">{role}</td>
                        <td className="p-3">
                          <Badge className={`
                            text-xs
                            ${mission.status === 'active' ? 'bg-emerald-500/20 text-emerald-400 border-none' : ''}
                            ${mission.status === 'completed' ? 'bg-slate-500/20 text-slate-400 border-none' : ''}
                            ${mission.status === 'briefing' ? 'bg-blue-500/20 text-blue-400 border-none' : ''}
                          `}>
                            {mission.status}
                          </Badge>
                        </td>
                        <td className="p-3 text-right text-amber-400 font-mono">{mission.xp_reward}</td>
                      </tr>);

                })}
                </tbody>
              </table>
            </div>
          }
        </CardContent>
      </Card>

      {/* Avatar Selection Dialog */}
      <Dialog open={avatarDialog} onOpenChange={setAvatarDialog}>
        <DialogContent data-testid="profile-profile-dialog" className="bg-slate-900 border-slate-800 text-slate-100 max-w-3xl">
          <DialogHeader>
            <DialogTitle>Choose Your Avatar</DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="marvel" className="mt-4">
            <TabsList className="grid w-full grid-cols-3 bg-slate-950 border-slate-800">
              <TabsTrigger value="marvel">Marvel</TabsTrigger>
              <TabsTrigger value="dc">DC Comics</TabsTrigger>
              <TabsTrigger value="upload">Upload</TabsTrigger>
            </TabsList>
            
            <TabsContent value="marvel" className="mt-6">
              <div className="grid grid-cols-3 gap-4">
                {marvelAvatars.map((avatarUrl, idx) =>
                <button
                  key={idx}
                  onClick={() => handleSelectAvatar(avatarUrl)}
                  className="aspect-square rounded-xl overflow-hidden border-2 border-slate-800 hover:border-indigo-500 transition-all hover:scale-105">

                    <img src={avatarUrl} alt={`Marvel Avatar ${idx + 1}`} className="w-full h-full object-cover" />
                  </button>
                )}
              </div>
            </TabsContent>

            <TabsContent value="dc" className="mt-6">
              <div className="grid grid-cols-3 gap-4">
                {dcAvatars.map((avatarUrl, idx) =>
                <button
                  key={idx}
                  onClick={() => handleSelectAvatar(avatarUrl)}
                  className="aspect-square rounded-xl overflow-hidden border-2 border-slate-800 hover:border-indigo-500 transition-all hover:scale-105">

                    <img src={avatarUrl} alt={`DC Avatar ${idx + 1}`} className="w-full h-full object-cover" />
                  </button>
                )}
              </div>
            </TabsContent>

            <TabsContent value="upload" className="mt-6">
              <div className="flex flex-col items-center justify-center py-12 border-2 border-dashed border-slate-700 rounded-xl">
                <Upload className="w-12 h-12 text-slate-500 mb-4" />
                <p className="text-slate-400 mb-4">Upload your own photo</p>
                <label htmlFor="avatar-upload" className="cursor-pointer">
                  <Button disabled={uploadingAvatar} asChild>
                    <span>
                      {uploadingAvatar ? 'Uploading...' : 'Choose File'}
                    </span>
                  </Button>
                  <input data-testid="profile-profile-input-6"
                    id="avatar-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden" />

                </label>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Training Request Dialog */}
      <TrainingRequestDialog
        open={trainingDialog}
        onClose={() => setTrainingDialog(false)}
        currentSkills={profile?.skills || []}
        officerEmail={currentUser?.email} />


        {/* Game Guide Dialog */}
        <GameGuideDialog
        open={gameGuideOpen}
        onClose={() => setGameGuideOpen(false)} />

      {/* Drive Video Dialog */}
      <Dialog open={driveVideoDialog} onOpenChange={setDriveVideoDialog}>
        <DialogContent data-testid="profile-profile-dialog-1" className="bg-slate-900 border-slate-800 text-slate-100 max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flame className="w-5 h-5 text-orange-400" />
              Understanding Officer's Drive
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <div className="aspect-video w-full rounded-lg overflow-hidden bg-black">
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/rbR2V1UeB_A"
                title="Officer's Drive Explanation"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen />

            </div>
          </div>
        </DialogContent>
      </Dialog>

        </div>);

}